/**
 * URL-state sort param parsers. Used by list pages that drive server-side
 * sort via URL search params (`?orderBy=X&orderDir=asc`).
 *
 * Lives here so the 9 list pages with sortable headers stop redeclaring the
 * identical pair of helpers. Each adopter still defines its own
 * `SortableColumn` literal-union and `SORTABLE_COLUMNS` allowed-set since
 * those vary per page; only the parsing logic is shared.
 */

/**
 * Parse `?orderBy=X` from URLSearchParams. Returns `defaultColumn` if the URL
 * value is missing or not in the allowed set.
 *
 * The generic `T` parameter is the local `SortableColumn` literal-union; the
 * `validColumns` set should contain the same string values, exposed as
 * `ReadonlySet<string>` for runtime membership checks.
 */
export function sortByFromParams<T extends string>(
  params: URLSearchParams,
  validColumns: ReadonlySet<string>,
  defaultColumn: T,
): T {
  const raw = params.get('orderBy') ?? '';
  return validColumns.has(raw) ? (raw as T) : defaultColumn;
}

/**
 * Parse `?orderDir=asc|desc` from URLSearchParams. Returns `defaultDir`
 * (default `'asc'`) if the URL value is missing or anything other than
 * `'asc'` / `'desc'`.
 */
export function sortDirFromParams(
  params: URLSearchParams,
  defaultDir: 'asc' | 'desc' = 'asc',
): 'asc' | 'desc' {
  const raw = params.get('orderDir');
  return raw === 'asc' || raw === 'desc' ? raw : defaultDir;
}

/**
 * Compute the next sort direction for a TableSortLabel click. If the user
 * clicks the column already sorted, flip the direction. Otherwise reset to
 * `'asc'` (the convention the entire app uses — clicking a new column always
 * starts ascending).
 *
 * Centralized so the 13 list pages with sortable headers stop redeclaring
 * the identical 2-line ternary inside their `handleSort`.
 */
export function nextSortDir<T>(
  newColumn: T,
  currentColumn: T,
  currentDir: 'asc' | 'desc',
): 'asc' | 'desc' {
  return newColumn === currentColumn ? (currentDir === 'asc' ? 'desc' : 'asc') : 'asc';
}
